import React from "react";
import ReactDOM from "react-dom/client";
import { AlertCircle, CheckCircle2, FileText, MessageSquare, Play, RefreshCw } from "lucide-react";
import { runCheck, submitFeedback } from "./api/client.js";
import type { CheckResult, CheckSource, FeedbackRequest, HostAdapter } from "./core/types.js";
import { resolveHost } from "./hosts/index.js";
import "./styles.css";

type LoadState = "idle" | "loading" | "success" | "error";

function App() {
  const [host, setHost] = React.useState<HostAdapter | null>(null);
  const [source, setSource] = React.useState<CheckSource>("selection");
  const [state, setState] = React.useState<LoadState>("idle");
  const [result, setResult] = React.useState<CheckResult | null>(null);
  const [error, setError] = React.useState("");
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);

  React.useEffect(() => {
    resolveHost()
      .then(setHost)
      .catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "宿主初始化失败。"));
  }, []);

  const handleCheck = React.useCallback(async () => {
    if (!host) {
      return;
    }

    setState("loading");
    setError("");

    try {
      const text = source === "selection" ? await host.getSelectionText() : await host.getDocumentText();
      if (!text.trim()) {
        throw new Error(source === "selection" ? "未读取到选区内容。" : "未读取到文档正文内容。");
      }

      const nextResult = await runCheck({ text, source, host: host.name });
      setResult(nextResult);
      setState("success");
    } catch (reason) {
      setState("error");
      setError(reason instanceof Error ? reason.message : "审查失败，请稍后重试。");
    }
  }, [host, source]);

  return (
    <main className="app-shell">
      <header className="topbar">
        <div>
          <h1>规范引用审查</h1>
          <p>{host ? hostLabel(host.name) : "正在识别宿主环境"}</p>
        </div>
        <button className="icon-button" type="button" onClick={() => setFeedbackOpen(true)} title="问题反馈">
          <MessageSquare size={18} />
        </button>
      </header>

      <section className="control-band" aria-label="审查控制">
        <div className="segmented">
          <button
            type="button"
            className={source === "selection" ? "active" : ""}
            onClick={() => setSource("selection")}
          >
            选区
          </button>
          <button
            type="button"
            className={source === "document" ? "active" : ""}
            onClick={() => setSource("document")}
          >
            全文
          </button>
        </div>
        <button className="primary-button" type="button" onClick={handleCheck} disabled={!host || state === "loading"}>
          {state === "loading" ? <RefreshCw size={17} className="spin" /> : <Play size={17} />}
          开始审查
        </button>
      </section>

      {error ? <StatusMessage tone="error" message={error} /> : null}
      {!result && !error ? <EmptyState /> : null}
      {result ? <ResultView result={result} /> : null}

      {feedbackOpen ? <FeedbackDialog onClose={() => setFeedbackOpen(false)} /> : null}
    </main>
  );
}

function ResultView({ result }: { result: CheckResult }) {
  return (
    <section className="results" aria-label="审查结果">
      <div className="summary-grid">
        <Metric label="总数" value={result.total} />
        <Metric label="通过" value={result.passed} tone="success" />
        <Metric label="未通过" value={result.failed} tone="danger" />
      </div>

      {result.issues.length === 0 ? (
        <div className="pass-panel">
          <CheckCircle2 size={22} />
          <span>当前内容未发现规范引用问题。</span>
        </div>
      ) : (
        <div className="issue-list">
          {result.issues.map((issue, index) => (
            <article className="issue-row" key={`${issue.normalizedRef}-${index}`}>
              <AlertCircle size={18} />
              <div>
                <h2>{issue.rawRef}</h2>
                {issue.name ? <p className="issue-name">《{issue.name}》</p> : null}
                <p>{issue.message}</p>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function Metric({ label, value, tone }: { label: string; value: number; tone?: "success" | "danger" }) {
  return (
    <div className={`metric ${tone || ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function StatusMessage({ message, tone }: { message: string; tone: "error" }) {
  return (
    <div className={`status ${tone}`} role="alert">
      <AlertCircle size={18} />
      <span>{message}</span>
    </div>
  );
}

function EmptyState() {
  return (
    <section className="empty-state">
      <FileText size={34} />
      <h2>选择内容后开始审查</h2>
      <p>结果会在任务窗格中保留，方便逐条核对。</p>
    </section>
  );
}

function FeedbackDialog({ onClose }: { onClose: () => void }) {
  const [form, setForm] = React.useState<FeedbackRequest>({
    feedbackType: "缺少规范/数据有误",
    content: "",
    contact: ""
  });
  const [otherType, setOtherType] = React.useState("");
  const [status, setStatus] = React.useState<LoadState>("idle");
  const [message, setMessage] = React.useState("");

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setStatus("loading");
    setMessage("");

    try {
      const feedbackType =
        form.feedbackType === "其他" ? `其他 - ${otherType.trim()}` : form.feedbackType;
      await submitFeedback({ ...form, feedbackType });
      setStatus("success");
      setMessage("感谢反馈，我们会尽快处理。");
    } catch (reason) {
      setStatus("error");
      setMessage(reason instanceof Error ? reason.message : "反馈提交失败。");
    }
  }

  return (
    <div className="dialog-backdrop" role="presentation">
      <form className="dialog" onSubmit={handleSubmit}>
        <header>
          <h2>问题反馈</h2>
          <button type="button" onClick={onClose} aria-label="关闭">
            ×
          </button>
        </header>

        <label>
          反馈类型
          <select
            value={form.feedbackType}
            onChange={(event) => setForm((current) => ({ ...current, feedbackType: event.target.value }))}
            required
          >
            <option>缺少规范/数据有误</option>
            <option>无法正常使用</option>
            <option>其他</option>
          </select>
        </label>

        {form.feedbackType === "其他" ? (
          <label>
            其他类型说明
            <input value={otherType} onChange={(event) => setOtherType(event.target.value)} required />
          </label>
        ) : null}

        <label>
          反馈内容
          <textarea
            value={form.content}
            onChange={(event) => setForm((current) => ({ ...current, content: event.target.value }))}
            required
          />
        </label>

        <label>
          联系方式
          <input
            value={form.contact}
            onChange={(event) => setForm((current) => ({ ...current, contact: event.target.value }))}
            required
          />
        </label>

        {message ? <p className={`dialog-message ${status}`}>{message}</p> : null}

        <footer>
          <button type="button" className="secondary-button" onClick={onClose}>
            取消
          </button>
          <button className="primary-button" type="submit" disabled={status === "loading"}>
            提交
          </button>
        </footer>
      </form>
    </div>
  );
}

function hostLabel(name: HostAdapter["name"]): string {
  if (name === "office-word") {
    return "Microsoft Word";
  }
  if (name === "wps-writer") {
    return "WPS Writer";
  }
  return "浏览器演示模式";
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
